import axios from "axios";
import { toast } from "sonner";
import { getDistributorReviewOwnerId } from "@/lib/distributorReview";
import { shouldSuppressLicenseBlockedToast } from "@/lib/licenseBlockedToastPolicy";

// Si REACT_APP_BACKEND_URL está vacío o no definido, usamos el mismo origen
// (caso Render.com / Emergent prod, donde el backend sirve también el frontend).
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BACKEND_URL}/api`;

// Tokens: localStorage si «Recordar dispositivo»; sessionStorage si sesión estricta (ver tokenStore.js).
import { tokenStore } from "@/lib/tokenStore";
export { tokenStore };

const api = axios.create({
  baseURL: API,
  withCredentials: true,
  /** Sin timeout las peticiones pueden colgarse indefinidamente (UI en spinner permanente). Subidas muy grandes pueden pasar `timeout` en la llamada. */
  timeout: 120000,
});

// Interceptor de petición: añade Authorization si tenemos token en storage del navegador.
api.interceptors.request.use((config) => {
  const t = tokenStore.get();
  if (t && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${t}`;
  }
  const reviewOwner = getDistributorReviewOwnerId();
  if (reviewOwner) {
    config.headers["X-Review-Owner-Id"] = reviewOwner;
  }
  // FormData: nunca fijar `Content-Type: multipart/form-data` a mano.
  // Sin el `boundary=…` FastAPI no parsea los archivos y la petición puede
  // quedar colgada o fallar con body vacío (spinner infinito en cargas).
  if (typeof FormData !== "undefined" && config.data instanceof FormData && config.headers) {
    const h = config.headers;
    if (typeof h.delete === "function") {
      h.delete("Content-Type");
      h.delete("content-type");
    } else {
      delete h["Content-Type"];
      delete h["content-type"];
    }
  }
  return config;
});

// ---- Interceptor: si el access_token expira, intenta refresh automáticamente.
// Si el refresh también falla, redirige al login limpiamente.
let isRefreshing = false;
let refreshQueue = [];
/** Evita múltiples toasts por bloqueo de licencia en la misma sesión de página. */
let licenseBlockedToastShown = false;
const processQueue = (err) => {
  refreshQueue.forEach(({ resolve, reject }) => err ? reject(err) : resolve());
  refreshQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    if (!original) return Promise.reject(error);
    const status = error.response?.status;
    const url = original.url || "";

    // No intentar refresh para los propios endpoints de auth
    // /auth/me también está en la lista: en el primer load (sin cookies), un 401 ahí
    // significa "no logueado" — no necesitamos hacer refresh, sólo mostrar /login.
    const isAuthCall = url.includes("/auth/login") || url.includes("/auth/refresh") ||
                       url.includes("/auth/me") ||
                       url.includes("/auth/forgot-password") || url.includes("/auth/reset-password") ||
                       url.includes("/auth/register") ||
                       url.includes("/auth/saas-register") ||
                       url.includes("/auth/password-recovery") ||
                       url.includes("/auth/designer-login") || url.includes("/auth/designer-reset");

    if (status === 401 && !original._retry && !isAuthCall) {
      if (isRefreshing) {
        // Ya hay un refresh en curso; encola esta petición para reintentarse luego
        return new Promise((resolve, reject) => {
          refreshQueue.push({ resolve, reject });
        }).then(() => api(original));
      }
      original._retry = true;
      isRefreshing = true;
      try {
        // Intenta refresh enviando el token también en el body por si la cookie
        // no está disponible (modo localStorage).
        const refreshTok = tokenStore.getRefresh();
        const { data } = await axios.post(
          `${API}/auth/refresh`,
          refreshTok ? { refresh_token: refreshTok } : {},
          { withCredentials: true },
        );
        // Si el backend devolvió tokens nuevos, actualízalos en localStorage.
        if (data?.access_token) {
          const remember =
            typeof data.remember === "boolean" ? data.remember : tokenStore.isPersistent();
          tokenStore.set(data.access_token, data.refresh_token || refreshTok, { remember });
        }
        processQueue(null);
        // Tras 401, reenviar multipart sin Content-Type fijo: si quedó `multipart/form-data`
        // sin boundary (o obsoleto), FastAPI recibe el archivo vacío y falla la activación por PDF.
        if (typeof FormData !== "undefined" && original.data instanceof FormData) {
          delete original.headers["Content-Type"];
        }
        return api(original);
      } catch (refreshErr) {
        processQueue(refreshErr);
        // Solo cerrar sesión si el servidor rechazó el refresh (401/403).
        // Red/timeout/502 al refrescar (p. ej. al generar PDFs/ZIP de contratos) no debe
        // expulsar al usuario: el access token en storage puede seguir siendo usable.
        const refreshStatus = refreshErr?.response?.status;
        if (refreshStatus === 401 || refreshStatus === 403) {
          tokenStore.clear();
          const detail = refreshErr.response?.data?.detail;
          const msg =
            (typeof detail === "string" && detail) ||
            detail?.message ||
            "Sesión expirada. Vuelve a iniciar sesión.";
          toast.error(msg, { duration: 12000 });
          try {
            window.dispatchEvent(new CustomEvent("visor:session-expired"));
          } catch {
            /* ignore */
          }
        }
        return Promise.reject(refreshErr);
      } finally {
        isRefreshing = false;
      }
    }
    if (
      status === 403 &&
      error.response?.data?.detail?.code === "LICENSE_BLOCKED" &&
      !url.includes("/system/license") &&
      !url.includes("/auth/saas-register")
    ) {
      if (!licenseBlockedToastShown && !shouldSuppressLicenseBlockedToast()) {
        licenseBlockedToastShown = true;
        const m = error.response.data.detail.message || "Licencia o periodo de prueba requerido.";
        toast.error(m, { duration: 10000 });
      }
    }
    return Promise.reject(error);
  }
);

/** Cuando responseType es "blob", los errores HTTP vienen como Blob JSON; extrae `detail`. */
export async function detailFromAxiosError(error) {
  const res = error.response;
  if (!res?.data && res?.data !== "") return "";
  const { data, status } = res;
  if (typeof data === "string") {
    try {
      const j = JSON.parse(data);
      return formatApiError(j.detail) || data;
    } catch {
      return data || (status === 401 ? "No autorizado" : "");
    }
  }
  if (typeof Blob !== "undefined" && data instanceof Blob) {
    try {
      const text = await data.text();
      try {
        const json = JSON.parse(text);
        return formatApiError(json.detail) || text.slice(0, 800);
      } catch {
        return text.slice(0, 800) || (status === 401 ? "No autorizado" : "");
      }
    } catch {
      return status === 401 ? "No autorizado" : "";
    }
  }
  if (data && typeof data === "object" && "detail" in data) {
    return formatApiError(data.detail);
  }
  return "";
}

export function formatApiError(detail) {
  // Devolver string vacío cuando no hay mensaje concreto del servidor evita
  // toasts genéricos de "Algo salió mal" que ensucian la UX en transiciones
  // (cold starts, refresh de token, etc.). Las pantallas hacen
  //   toast.error(formatApiError(...) || "Mensaje de respaldo")
  // así que devolver "" deja que cada pantalla decida si vale la pena alertar.
  if (detail == null) return "";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  if (detail && typeof detail === "object" && !Array.isArray(detail)) {
    if (typeof detail.message === "string" && detail.message) {
      let out = detail.message;
      if (typeof detail.hint === "string" && detail.hint) {
        out += `\n\n${detail.hint}`;
      }
      if (
        typeof detail.signing_fingerprint_in_pdf === "string" &&
        detail.signing_fingerprint_in_pdf
      ) {
        out += `\n\nHuella en el PDF (emisor): ${detail.signing_fingerprint_in_pdf}`;
      }
      if (
        typeof detail.signing_fingerprint_this_installation === "string" &&
        detail.signing_fingerprint_this_installation
      ) {
        out += `\nHuella en esta instalación: ${detail.signing_fingerprint_this_installation}`;
      }
      return out;
    }
    if (detail.code === "LICENSE_BLOCKED" && typeof detail.message === "string") return detail.message;
  }
  try {
    return JSON.stringify(detail);
  } catch {
    return String(detail);
  }
}

export const fmtMoney = (n) =>
  new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(Number(n) || 0);

export const fmtNumber = (n) =>
  new Intl.NumberFormat("es-MX").format(Number(n) || 0);

export const tipoLabel = (t) => ({ I: "Ingreso", E: "Egreso", P: "Pago", N: "Nómina", T: "Traslado" }[t] || t);

/** Etiqueta de tipo en visor SAT: el SAT llama «Ingreso» (I) también a facturas de gasto recibidas. */
export const visorCfdiTipoLabel = (tipo, direccion) => {
  const t = String(tipo || "").toUpperCase();
  const d = String(direccion || "").toLowerCase();
  if (t === "I") {
    if (d === "recibido") return "Factura recibida";
    if (d === "emitido") return "Factura emitida";
    return "Factura (I)";
  }
  return tipoLabel(t);
};

export const tipoColor = (t) => {
  const map = {
    I: "bg-emerald-50 text-emerald-700 border-emerald-200",
    Ingreso: "bg-emerald-50 text-emerald-700 border-emerald-200",
    E: "bg-red-50 text-red-700 border-red-200",
    Egreso: "bg-red-50 text-red-700 border-red-200",
    P: "bg-blue-50 text-blue-700 border-blue-200",
    Pago: "bg-blue-50 text-blue-700 border-blue-200",
    N: "bg-violet-50 text-violet-700 border-violet-200",
    T: "bg-amber-50 text-amber-700 border-amber-200",
  };
  return map[t] || "bg-slate-50 text-slate-700 border-slate-200";
};

export default api;
